import { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { format } from 'date-fns/esm';
import type { Table } from 'schema-dts';
import { Text } from '@simplywallst/ui-core';
import getManagementSelector from '@/pages/CompanyReport/components/Management/redux/selectors';
import DocumentSchema from '@components/DocumentSchema';
import type { State as CompanyState } from '@/pages/CompanyReport/redux/interface';
import { CEOCompensationDataTable, TableRow } from './styled';
import { getUnitValue } from '../../utils';

interface ReduxState {
  company: CompanyState;
}

function selector(state: ReduxState) {
  const management = getManagementSelector(state, {});

  return {
    totalCompensation: management.totalCompensation,
    salary: management.salary,
    earnings: management.earnings,
    currencyISO: management.currencyISO,
    reportingUnit: management.reportingUnit,
    ceoName: `${management.leaderSalutation} ${management.leaderLastName}`,
    companyName: management.companyName,
  };
}

export const CEOCompensationTable = () => {
  const {
    totalCompensation,
    salary,
    earnings,
    currencyISO,
    reportingUnit,
    ceoName,
    companyName,
  } = useSelector<Parameters<typeof selector>[0], ReturnType<typeof selector>>(
    selector,
    shallowEqual
  );
  const data = useMemo(() => {
    return earnings.reverse().map((item) => {
      const compensationItem = totalCompensation.find(
        (entry) => entry[0] === item[0]
      );
      const salaryItem = salary.find((entry) => entry[0] === item[0]);

      return {
        time: format(item[0], 'MMM dd yyyy'),
        earnings: item[1],
        totalCompensation: compensationItem ? compensationItem[1] : undefined,
        salary: salaryItem ? salaryItem[1] : undefined,
      };
    });
  }, [earnings, salary, totalCompensation]);

  const header = `${ceoName}'s Compensation vs ${companyName} Earnings`;
  const caption = `How has ${ceoName}'s remuneration changed compared to ${companyName}'s earnings?`;

  return (
    <>
      <DocumentSchema<Table>
        item={{
          '@context': 'https://schema.org',
          '@type': 'Table',
          headline: header,
          about: caption,
        }}
      />
      <CEOCompensationDataTable
        columnTextAlignments="l"
        data-cy-id="ceo-compensation-table"
      >
        <caption data-cy-id="ceo-compensation-table-caption">{caption}</caption>
        <thead>
          <tr>
            <th>Date</th>
            <th>Total Comp.</th>
            <th>Salary</th>
            <th>Company Earnings</th>
          </tr>
        </thead>
        <tbody>
          {data.map(({ time, totalCompensation, salary, earnings }, i) => {
            const earningsColor = earnings > 0 ? 'good' : 'bad';

            return (
              <TableRow
                selected={i === 0}
                key={time}
                data-cy-id={`ceo-compensation-table-row-${i}`}
              >
                <td>{time}</td>
                <td>
                  {totalCompensation
                    ? getUnitValue({
                        unit: 'total compensation',
                        value: totalCompensation,
                        currencyISO,
                        reportingUnit,
                      })
                    : 'n/a'}
                </td>
                <td>
                  {salary
                    ? getUnitValue({
                        unit: 'salary',
                        value: salary,
                        currencyISO,
                        reportingUnit,
                      })
                    : 'n/a'}
                </td>
                <td>
                  <Text typography="inherit" color={earningsColor}>
                    {getUnitValue({
                      unit: 'company earnings',
                      value: earnings,
                      currencyISO,
                      reportingUnit,
                    })}
                  </Text>
                </td>
              </TableRow>
            );
          })}
        </tbody>
      </CEOCompensationDataTable>
    </>
  );
};
